<script context="module" lang="ts">
  import { readable, writable, type Readable } from 'svelte/store';

  export let isVisible = writable(false);

  type ProUnlockedModalParams = {
    onUnlocked?: (() => Promise<void>) | (() => void) | null;
  };
  const selDefault: ProUnlockedModalParams = {
    onUnlocked: null
  };
  const sel = writable(selDefault);

  export function openProUnlockedModal(params: ProUnlockedModalParams) {
    const defaultParams = { ...selDefault, ...params };
    sel.set(defaultParams);
    isVisible.set(true);
  }
</script>

<script lang="ts">
  import type { App } from '$lib/types';
  import { IRK_API_URL, STATIC_URL } from '$lib/constants';
  import { createEventDispatcher, getContext } from 'svelte';

  import { Dialog } from '$mds/Dialogs';
  import Button from '$mds/Buttons/Button/Button.svelte';
  import BadgeCore from '$assets/svgs/badges/core.svg?component';
  import BadgePro from '$assets/svgs/badges/pro.svg?component';
  import IconWrapper from '$mds/IconWrapper/IconWrapper.svelte';
  import { faDiscord } from '@fortawesome/free-brands-svg-icons';
  import { getApolloClient, query } from '$lib/apollo';
  import { GET_CONNECTED_ACCOUNTS } from '$graphql/queries/settings';
  import Account from '$routes/(nav)/account/settings/_components/Account.svelte';
  import Tag from '$mds/Tag/Tag.svelte';
  import { gql } from '$lib/apollo';
  import { Toasts } from '$lib/stores';

  const { isCore } = getContext<App>('app');
  const dispatch = createEventDispatcher();

  let connectionResponse: Readable<any> = readable(null);

  let hasJoinedDiscord = false;

  async function checkHasJoinedDiscord() {
    const { data } = await getApolloClient().query({
      query: gql`
        query GetHasJoinedDiscord {
          hasJoinedDiscord(metafy: true)
        }
      `,
      fetchPolicy: 'network-only'
    });
    hasJoinedDiscord = data?.hasJoinedDiscord;

    if (!hasJoinedDiscord) {
      window.setTimeout(() => {
        checkHasJoinedDiscord();
      }, 5000);
    }
  }

  let syncRolesLoading = false;
  async function discordSyncRoles() {
    try {
      syncRolesLoading = true;
      await getApolloClient().mutate({
        mutation: gql`
          mutation DiscordSyncRoles($input: DiscordSyncRolesInput!) {
            discordSyncRoles(input: $input) {
              result {
                id
              }
            }
          }
        `,
        variables: {
          input: {
            metafy: true
          }
        }
      });
      Toasts.push({
        kind: 'success',
        content: 'Role successfully claimed! Check the Discord server!'
      });
      window.setTimeout(() => {
        $isVisible = false;
      }, 1500);
    } catch (err) {
      Toasts.push({
        kind: 'error',
        content: "Couldn't sync the role, please try again"
      });
    } finally {
      syncRolesLoading = false;
    }
  }

  let dialog;

  function onComplete() {
    $sel.onUnlocked?.();
    dialog?.closeModal();
    dispatch('unlocked');
  }
</script>

<Dialog
  bind:dialog
  bind:isOpen={$isVisible}
  class="{$isCore ? 'core-upgrade-modal' : 'pro-upgrade-modal'} -inset-px top-auto transform-gpu !border-transparent"
  style="--bg-image: url('{STATIC_URL}/membership/Noise_tile.png')"
  dialogClass="{$isCore ? 'core-upgrade-backdrop' : 'pro-upgrade-backdrop'} overflow-visible w-[700px]"
  closeButton={false}
  allowClickOutside={false}
  on:open={() => {
    connectionResponse = query({
      query: GET_CONNECTED_ACCOUNTS,
      data: {}
    });

    checkHasJoinedDiscord();
  }}
>
  <svelte:component
    this={$isCore ? BadgeCore : BadgePro}
    class="absolute left-1/2 top-0 size-12 -translate-x-1/2 -translate-y-1/2"
  />

  <div class="mt-4 flex flex-col items-center">
    <h3 class="text-display-sm text-ondark-primary mb-4">
      Welcome to Metafy {$isCore ? 'Core' : 'Pro'}
    </h3>
    <p class="text-body-xs text-ondark-primary mb-2 p-4 text-left">
      {#if $isCore}
        You now have exclusive discord access to <span class="text-[#2C88DD]">The Lounge</span>. Think of it as your
        favorite bar in town. Partners only (no outsiders). Nice, but not pretentious. Good people. Great stories.
        Everybody knows everybody else’s name.
        <br /><br />This is your unfair advantage.
      {:else}
        You now have access to the <span class="text-[#B75BFF]">Tea Room</span>. Some say the great minds in the Tea
        Room have a hand in shaping the very future of Metafy and gaming as a whole. We'll let you decide.
        <br /><br />Be warned, dangerous, illogical experiments happen in the Tea Room. So, proceed at your own risk.
      {/if}
    </p>

    <div class="self-start p-4">
      <div class="mb-3 flex items-center gap-x-2">
        <IconWrapper size="base" theme="gradient" icon={faDiscord} />
        <p class="bg-gradient-shimmer text-body-md bg-clip-text text-left text-transparent">
          Redeem your exclusive Discord access
        </p>
      </div>

      <div class="mt-3 flex flex-col gap-y-4 text-left">
        {#if !$connectionResponse?.data?.currentSession?.discord}
          <Account
            type="connect"
            provider="discord"
            customProviderText="First, connect your Discord account!"
            providerParams={[]}
            backgroundColor=""
            border="border border-ondark-subtle"
            on:connect={() => {
              $connectionResponse.query?.refetch();
            }}
          />
        {/if}
        <div class="flex gap-x-4 text-left">
          <div class="border-ondark-subtle flex flex-1 flex-col gap-y-4 rounded-lg border p-4">
            <div class="flex items-center gap-x-2">
              <Tag
                variant={hasJoinedDiscord || !$connectionResponse?.data?.currentSession?.discord
                  ? 'secondary'
                  : 'positive'}
                size="md"
                label="Step 1"
              />
              <p class="text-body-xs text-ondark-primary">Join the Discord</p>
            </div>
            <p class="text-body-2xs text-ondark-subtle">
              A giant, underground rendezvous of the greatest minds in gaming, far from the reek of social media’s
              sewers.
            </p>
            <Button
              disabled={hasJoinedDiscord || !$connectionResponse?.data?.currentSession?.discord}
              variant="primary"
              size="sm"
              width="full"
              on:click={() => {
                window.open('https://discord.mfy.gg', '_blank');
              }}
            >
              Join Discord server
            </Button>
          </div>
          <div class="border-ondark-subtle flex flex-1 flex-col gap-y-4 rounded-lg border p-4">
            <div class="flex items-center gap-x-2">
              <Tag variant={hasJoinedDiscord ? 'positive' : 'secondary'} size="md" label="Step 2" />
              <p class="text-body-xs text-ondark-primary">
                {$isCore ? 'Enter The Lounge' : 'Enter The Tea Room'}
              </p>
            </div>
            <p class="text-body-2xs text-ondark-subtle">
              An exclusive discord channel for our partners. Get the inside scoop on our business, advice on how to grow
              and more.
            </p>
            <Button
              variant="primary"
              class="mt-auto"
              disabled={!hasJoinedDiscord}
              size="sm"
              width="full"
              loading={syncRolesLoading}
              on:click={() => {
                discordSyncRoles();
              }}
              >Claim role
            </Button>
          </div>
        </div>
      </div>
    </div>

    <Button
      variant="ghost"
      size="md"
      width="min"
      title="Let's go!"
      class="absolute -bottom-14 col-span-3 w-48"
      on:click={onComplete}
    >
      Skip this, I’ll join later
    </Button>
  </div>
</Dialog>

<style lang="postcss">
  :global(.core-upgrade-modal) {
    background:
      linear-gradient(theme('backgroundColor.ondark.base/0.8'), theme('backgroundColor.ondark.base/0.8')) padding-box,
      linear-gradient(120deg, #2c88dda8 15%, #55bbe70d 48%, #a078f00d 48%, #2c88dda8 80%) border-box;
  }
  :global(.pro-upgrade-modal) {
    background:
      linear-gradient(theme('backgroundColor.ondark.base/0.8'), theme('backgroundColor.ondark.base/0.8')) padding-box,
      linear-gradient(120deg, #b75bffa8 15%, #55bbe70d 48%, #a078f00d 48%, #b75bffa8 80%) border-box;
  }
  :global(.core-upgrade-backdrop::backdrop) {
    --bgpos-blue: 35% 40%;
    --bgpos-purple: 65% 60%;
    --bgsize: 50% 50%;
    @apply animate-fade-in-slow;
    background-image:
      radial-gradient(var(--bgsize) at var(--bgpos-blue), #2c88dd91 0%, transparent 100%),
      radial-gradient(var(--bgsize) at var(--bgpos-purple), #2c88dd91 0%, transparent 100%);
    background-blend-mode: overlay;
  }
  :global(.pro-upgrade-backdrop::backdrop) {
    --bgpos-blue: 35% 40%;
    --bgpos-purple: 65% 60%;
    --bgsize: 50% 50%;
    @apply animate-fade-in-slow;
    background-image:
      radial-gradient(var(--bgsize) at var(--bgpos-blue), #b75bff91 0%, transparent 100%),
      radial-gradient(var(--bgsize) at var(--bgpos-purple), #b75bff91 0%, transparent 100%);
    background-blend-mode: overlay;
  }
  @media not all and screen(sm) {
    :global(.pro-upgrade-backdrop::backdrop),
    :global(.core-upgrade-backdrop::backdrop) {
      --bgpos-blue: 25% 80%;
      --bgpos-purple: 75% 80%;
      --bgsize: 80% 60%;
    }
  }
  :global(.pro-upgrade-modal::after),
  :global(.core-upgrade-modal::after) {
    content: '';
    @apply pointer-events-none absolute inset-0 rounded-[inherit] bg-[var(--bg-image)] mix-blend-lighten;
  }
</style>
